import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X, FileText, CheckCircle, Sparkles, Languages } from 'lucide-react';

const PROJECT_TYPES = [
  { id: 'grammar', label: 'Grammar Checker', icon: <CheckCircle size={24} />, desc: 'Start a blank document with live grammar suggestions.' },
  { id: 'translate', label: 'Translator', icon: <Languages size={24} />, desc: 'Translate or adapt your writing.' },
  { id: 'summarize', label: 'Summariser', icon: <FileText size={24} />, desc: 'Summarise long text quickly.' },
  { id: 'ai_rewrite', label: 'AI Writer', icon: <Sparkles size={24} />, desc: 'Change tone and improve selected writing.' },
  { id: 'tagline', label: 'Tagline Generator', icon: <Sparkles size={24} />, desc: 'Create catchy taglines.' },
  { id: 'abbreviations', label: 'Abbreviation Checker', icon: <FileText size={24} />, desc: 'Find abbreviations in text and list their full forms alphabetically.' },
];

const NewProjectModal = ({ onClose, onSelect, folderId }) => {
  const navigate = useNavigate();

  const handleSelect = (id) => {
    const folderParam = folderId ? `&folder=${folderId}` : '';
    navigate(`/editor?type=${id}&new=${Date.now()}${folderParam}`);
    onSelect?.();
    onClose?.();
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>Create New Project</h2>
          <button style={styles.closeBtn} onClick={onClose}><X size={20} /></button>
        </div>
        <p style={styles.subtitle}>Select the type of project you want to start.</p>
        
        <div style={styles.grid}>
          {PROJECT_TYPES.map((type) => (
            <button type="button"
              key={type.id} 
              style={styles.card} 
              onClick={() => handleSelect(type.id)}
              onMouseOver={(e) => e.currentTarget.style.borderColor = '#3b82f6'}
              onMouseOut={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
            >
              <div style={styles.iconWrapper}>{type.icon}</div>
              <h3 style={styles.cardTitle}>{type.label}</h3>
              <p style={styles.cardDesc}>{type.desc}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.4)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  },
  modal: {
    backgroundColor: '#ffffff',
    borderRadius: '24px',
    padding: '32px',
    width: '100%',
    maxWidth: '640px',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    animation: 'slideUp 0.3s ease-out',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: 700,
    color: '#0f172a',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: '#64748b',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    transition: 'background 0.2s',
  },
  subtitle: {
    color: '#64748b',
    fontSize: '15px',
    margin: '0 0 24px 0',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '16px',
  },
  card: {
    textAlign: 'left',
    font: 'inherit',
    border: '1px solid #e2e8f0',
    borderRadius: '16px',
    padding: '20px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    backgroundColor: '#f8fafc',
  },
  iconWrapper: {
    color: '#4f46e5',
    marginBottom: '12px',
    background: '#e0e7ff',
    padding: '10px',
    borderRadius: '12px',
  },
  cardTitle: {
    margin: '0 0 4px 0',
    fontSize: '16px',
    fontWeight: 600,
    color: '#1e293b',
  },
  cardDesc: {
    margin: 0,
    fontSize: '13px',
    color: '#64748b',
    lineHeight: 1.4,
  },
};

export default NewProjectModal;
